import { toaster } from '@/components/ui/toaster';
import { mcpQueryKeys } from '@/hooks/queries/use-mcp-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  McpConsentDecision,
  McpConsentDecisionResponse,
  McpConnectionReduction,
  McpConnectionMutationResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';
import { trackProductAction } from '@/libs/product-analytics';

export function useMcpDecisionAction(transaction: string) {
  const { t } = useTranslation();
  return useMutation<McpConsentDecisionResponse, AxiosError<ApiResponse>, McpConsentDecision>({
    mutationFn: (payload) =>
      trackProductAction(
        'assistants',
        payload.decision === 'allow' ? 'connect' : 'decline',
        async () => {
          const { data } = await api.post<ApiResponse<McpConsentDecisionResponse>>(
            `/mcp/oauth/transactions/${transaction}/decision`,
            payload,
            { withCredentials: true }
          );
          if (!data.data) throw new Error('Missing consent decision');
          return data.data;
        }
      ),
    onError: (error) => {
      // A plan refusal opens the upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      toaster.error({
        title: t('common.error'),
        description: error.response?.data?.message || t('mcp.consent.decisionError'),
      });
    },
  });
}

export function useMcpConnectionAction(id: string) {
  const client = useQueryClient();
  const { t } = useTranslation();
  return useMutation<
    McpConnectionMutationResponse,
    AxiosError<ApiResponse>,
    McpConnectionReduction | null
  >({
    mutationFn: async (payload) => {
      const response = payload
        ? await api.patch<ApiResponse<McpConnectionMutationResponse>>(
            `/account/connected-assistants/${id}`,
            payload,
            { withCredentials: true }
          )
        : await api.delete<ApiResponse<McpConnectionMutationResponse>>(
            `/account/connected-assistants/${id}`,
            { withCredentials: true }
          );
      if (!response.data.data) throw new Error('Missing connection response');
      return response.data.data;
    },
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: mcpQueryKeys.connections });
      toaster.success({
        title: t(data.revoked ? 'mcp.settings.revokedSuccess' : 'mcp.settings.updatedSuccess'),
      });
    },
    onError: (error) =>
      toaster.error({
        title: t('common.error'),
        description: error.response?.data?.message || t('mcp.settings.mutationError'),
      }),
  });
}
